import { supabase } from '../lib/supabaseClient';

export type BinanceMarketType = 'spot' | 'futures';
export type BinanceOrderSide = 'BUY' | 'SELL';
export type BinanceOrderType = 'MARKET' | 'LIMIT';

export interface BinanceConnection {
  id: string;
  exchange: 'binance';
  label: string;
  is_testnet: boolean;
  status: 'pending' | 'connected' | 'error' | 'disabled';
  permissions: {
    canTrade?: boolean;
    canWithdraw?: boolean;
    canDeposit?: boolean;
  };
  supported_market_types: string[];
  last_verified_at: string | null;
  last_error: string | null;
  created_at: string;
}

export interface BinanceSpotBalance {
  asset: string;
  free: string;
  locked: string;
}

export interface BinanceFuturesBalance {
  asset: string;
  balance: string;
  availableBalance: string;
  crossWalletBalance?: string;
  crossUnPnl?: string;
}

export interface BinanceAccountOverview {
  spotAccount?: {
    accountType?: string;
    canTrade?: boolean;
    canWithdraw?: boolean;
    canDeposit?: boolean;
    balances?: BinanceSpotBalance[];
  } | null;
  futuresAccount?: {
    totalWalletBalance?: string;
    totalMarginBalance?: string;
    availableBalance?: string;
    totalUnrealizedProfit?: string;
  } | null;
  futuresBalances?: BinanceFuturesBalance[];
  errors?: {
    spot?: string | null;
    futuresAccount?: string | null;
    futuresBalances?: string | null;
  };
}

interface BinanceFunctionResponse<T> {
  success: boolean;
  error?: string;
  connection?: BinanceConnection;
  connections?: BinanceConnection[];
  order?: T;
  data?: T;
  spotAccount?: T;
  futuresAccount?: T;
  futuresBalances?: T;
  errors?: BinanceAccountOverview['errors'];
  transfer?: T;
}

const invokeBinance = async <T>(action: string, payload: Record<string, unknown> = {}) => {
  const { data, error } = await supabase.functions.invoke<BinanceFunctionResponse<T>>('binance-exchange', {
    body: { action, ...payload }
  });

  if (error) {
    throw new Error(error.message || 'Binance function failed');
  }

  if (!data?.success) {
    throw new Error(data?.error || 'Binance request failed');
  }

  return data;
};

export const binanceExchangeApi = {
  async listConnections() {
    const response = await invokeBinance<never>('listConnections');
    return response.connections || [];
  },

  async connect(params: {
    label: string;
    apiKey: string;
    apiSecret: string;
    isTestnet: boolean;
  }) {
    const response = await invokeBinance<never>('connect', params);
    if (!response.connection) throw new Error('Binance connection was not returned');
    return response.connection;
  },

  async disconnect(connectionId: string) {
    await invokeBinance<never>('disconnect', { connectionId });
  },

  async getAccountOverview(connectionId: string): Promise<BinanceAccountOverview> {
    const response = await invokeBinance<never>('accountOverview', { connectionId });
    return {
      spotAccount: response.spotAccount as BinanceAccountOverview['spotAccount'],
      futuresAccount: response.futuresAccount as BinanceAccountOverview['futuresAccount'],
      futuresBalances: (response.futuresBalances as BinanceFuturesBalance[]) || [],
      errors: response.errors
    };
  },

  async placeSpotOrder(params: {
    connectionId: string;
    symbol: string;
    side: BinanceOrderSide;
    orderType: BinanceOrderType;
    quantity: string;
    price?: string;
  }) {
    const response = await invokeBinance('spotPlaceOrder', params);
    return response.order;
  },

  async convertSpotAssetToUsdt(params: {
    connectionId: string;
    asset: string;
    amount: string;
  }) {
    const response = await invokeBinance('convertSpotAssetToUsdt', params);
    return response.order;
  },

  async placeFuturesOrder(params: {
    connectionId: string;
    symbol: string;
    side: BinanceOrderSide;
    orderType: BinanceOrderType;
    quantity: string;
    price?: string;
    leverage?: string;
    marginType?: 'isolated' | 'cross';
    reduceOnly?: boolean;
  }) {
    const response = await invokeBinance('futuresPlaceOrder', params);
    return response.order;
  },

  async getOpenOrders(connectionId: string, marketType: BinanceMarketType, symbol?: string) {
    const response = await invokeBinance('openOrders', { connectionId, marketType, symbol });
    return response.data || [];
  },

  async getOrderHistory(connectionId: string, marketType: BinanceMarketType, symbol?: string) {
    const response = await invokeBinance('orderHistory', { connectionId, marketType, symbol });
    return response.data || [];
  },

  async transfer(params: {
    connectionId: string;
    transferType: string;
    asset: string;
    amount: string;
  }) {
    const response = await invokeBinance('transfer', params);
    return response.transfer;
  }
};
